/**
 * Algolia `wabunka_items` インデックスの 1 レコード分の型定義。
 * バックエンド側のスキーマ（wabunka-backend/pkg/application/dto/algolia_sync_dto.go）と
 * Design Doc「キーワード検索」UC1 に対応する。
 */
import { ITINERARY_TAG_DAYS_THRESHOLD, ITINERARY_TAG_LABEL_MULTI_DAY, ITINERARY_TAG_LABEL_SHORT_STAY } from '@/wabunka-src/features/Itinerary/constants/itineraryTagLabels';

export type AlgoliaTagGroupType = 'destination' | 'theme' | 'preference' | 'category' | string;

export type AlgoliaFeatureCategoryLabel = 'destination' | 'itinerary' | 'theme' | 'area' | 'category' | string;

export interface AlgoliaTagPayload {
  name: string;
  webPath: string;
  tagGroupType: AlgoliaTagGroupType;
}

export interface AlgoliaExperiencePayload {
  name: string;
  slug: string;
  thumbnailUrl: string;
  location: string;
  durationMinutes: number;
  price: number;
}

// Destination × Theme ページ（例: /kyoto/tea-ceremony）を表す feature の categoryLabel。
// microCMS の type: destination-theme エントリ由来（JCAT-5557）。専用レコード種は作らず feature に相乗りする。
export const DESTINATION_THEME_CATEGORY_LABEL = 'destination-theme';

export interface AlgoliaFeaturePayload {
  title: string;
  slug: string;
  thumbnailUrl: string;
  categoryLabel: AlgoliaFeatureCategoryLabel;
  // itinerary の categoryLabel 時に microCMS の itineraryTagSettings.days を載せる。
  // 載っていない場合は Multi-Day Journey の文言にフォールバックする。
  days?: number;
  // categoryLabel === 'destination-theme' のときの遷移先パス（/{destination}/{theme}）。JCAT-5557。
  // webPath のトークン化で destination 語・theme 語の双方にヒットする（searchableAttributes に含める運用）。
  webPath?: string;
}

interface AlgoliaHitBase {
  objectID: string;
  __queryID?: string;
  __position?: number;
}

export interface AlgoliaTagHit extends AlgoliaHitBase {
  type: 'tag';
  tag: AlgoliaTagPayload;
}

export interface AlgoliaExperienceHit extends AlgoliaHitBase {
  type: 'experience';
  experience: AlgoliaExperiencePayload;
}

export interface AlgoliaFeatureHit extends AlgoliaHitBase {
  type: 'feature';
  feature: AlgoliaFeaturePayload;
}

export type AlgoliaHit = AlgoliaTagHit | AlgoliaExperienceHit | AlgoliaFeatureHit;

export const isTagHit = (hit: AlgoliaHit): hit is AlgoliaTagHit => hit.type === 'tag';
export const isExperienceHit = (hit: AlgoliaHit): hit is AlgoliaExperienceHit => hit.type === 'experience';
export const isFeatureHit = (hit: AlgoliaHit): hit is AlgoliaFeatureHit => hit.type === 'feature';

// webPath の表記揺れ（先頭/末尾スラッシュ）を吸収して正規化する（先頭/末尾スラッシュを除去した文字列を返す）。
// 遷移先パス（buildDestinationThemePath）・候補判定（isDestinationThemeFeatureHit）・GA 帰属（clicked_slug）で
// 同じ正規化規則を共有し、遷移先とログの値がずれないようにする。JCAT-5557。
export const normalizeWebPath = (webPath: string | undefined): string => (webPath ?? '').replace(/^\/+|\/+$/g, '');

// Destination × Theme ページ候補（feature かつ categoryLabel === 'destination-theme' かつ webPath あり）の判定。
// 専用レコード種ではなく feature に相乗りしているため categoryLabel で見分ける（JCAT-5557）。
// webPath 必須なのは重要: backend が webPath を付与する前の既存 destination-theme レコード（webPath 無し）は
// 従来どおり非表示に保ち、フロント先行リリースを no-op にするため（遷移先 '/' の行が出るのを防ぐ）。
// 正規化後に空になる webPath（'/' や '///' 等）も除外する。これらは buildDestinationThemePath で '/' に
// 変換されトップページへ遷移してしまうため、候補として表示しない。
export const isDestinationThemeFeatureHit = (hit: AlgoliaHit): hit is AlgoliaFeatureHit =>
  isFeatureHit(hit) && hit.feature.categoryLabel === DESTINATION_THEME_CATEGORY_LABEL && normalizeWebPath(hit.feature.webPath) !== '';

// destination 名の索引を組み立てるための最小の入力形（WabunkaTagViewModel を構造的に受ける）。
interface DestinationTagLike {
  name: string;
  webPath: string;
  tagGroupType: string;
}

// destination の slug → タグ名の索引。同一タグが複数タググループに所属して重複することがあるが、
// name は同じなので Map で畳んで問題ない。webPath の表記揺れは normalizeWebPath で吸収する。
export const buildDestinationNameBySlug = (tags: readonly DestinationTagLike[]): ReadonlyMap<string, string> =>
  new Map(tags.filter((tag) => tag.tagGroupType === 'destination').map((tag) => [normalizeWebPath(tag.webPath), tag.name]));

// slug をそのまま人が読める形に落とす最後の手段（例: mt-fuji-around → Mt Fuji Around）。
// タグ名が引けなかったときのみ使う。記号・大小文字は復元できないので正確な表示ではない。
const titleCaseSlug = (slug: string): string =>
  slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Destination × Theme 候補のカテゴリチップ表示。webPath の先頭セグメント（destination の slug）から
// tags API のタグ名を引く（例: /mt-fuji-around/tea-ceremony → 'Mt.Fuji & Around'）。
// 遷移先の DestinationTheme ページが見出しに使う名前（tagDictByWebPath[destination].name）と同じ値になるため、
// 候補行と遷移先で表記が揃う。slug の title case 化では 'Mt.Fuji & Around' や 'Other places (Tokyo & around)' の
// ような記号入りのタグ名を復元できないため、索引の正引きを主経路にする。JCAT-5557。
// タグが引けない場合（タグ削除・slug 変更等のデータ乖離）は slug の title case にフォールバックする。
// destination セグメント自体が取れない場合（isDestinationThemeFeatureHit を通していない hit）は
// 空チップを避けるため汎用文言にする。
export const buildDestinationThemeCategoryLabel = (hit: AlgoliaFeatureHit, destinationNameBySlug: ReadonlyMap<string, string>): string => {
  const [destinationSlug = ''] = normalizeWebPath(hit.feature.webPath).split('/');
  if (destinationSlug === '') return 'Explore by Theme';
  return destinationNameBySlug.get(destinationSlug) ?? titleCaseSlug(destinationSlug);
};

// Figma の表示文言。area / category の行は SearchResults 側で非表示なので、これらのラベルは索引に値が残った場合の保険。
// 未知ラベルは元の文字列をそのまま返す。
// itinerary はトップページの KV と同じ日数別表記に揃える（days 未指定なら Multi-Day Journey）。
export const getFeatureCategoryDisplayLabel = (hit: AlgoliaFeatureHit): string => {
  const { categoryLabel, days } = hit.feature;
  switch (categoryLabel) {
    case 'destination':
      return 'Featured Destination';
    case 'itinerary':
      if (days !== undefined && days <= ITINERARY_TAG_DAYS_THRESHOLD) return ITINERARY_TAG_LABEL_SHORT_STAY;
      return ITINERARY_TAG_LABEL_MULTI_DAY;
    case 'theme':
      return 'Explore by Theme';
    case 'area':
      return 'Area';
    case 'category':
      return 'Explore by Theme';
    default:
      return categoryLabel;
  }
};
